import React, { useState, useEffect, useRef } from "react";
import { Typography, Box, makeStyles, Button } from "@material-ui/core";
import {
  CardNumberElement,
  useStripe,
  useElements,
  IbanElement
} from "@stripe/react-stripe-js";
import { Link as RouterLink, useHistory, Redirect } from "react-router-dom";
import ReCAPTCHA from "react-google-recaptcha";

import { ENVIRONMENT } from "../../data/constants";

// Icons
import LockIcon from "@material-ui/icons/Lock";
import { useSelector, connect } from "react-redux";
import { useSnackbar } from "notistack";
import {
  remCalc,
  trimSpaces,
  validateEmail,
  validateName
} from "../../../utils/helpers";
import API from "../../../utils/axios";
import {
  setPayment,
  setInvoices,
  setBillingDetails
} from "../../../store/actions/account";
import { setUser } from "../../../store/actions/auth";
import StripeForm from "./StripeForm";
import { plans } from "../../data/plans";
import {
  getProjectDetails,
  resetProject
} from "../../../store/actions/project";
import Prorata from "./Prorata";

const useStyles = makeStyles(theme => ({
  btn: {
    textTransform: "none",
    paddingRight: theme.spacing(3),
    paddingLeft: theme.spacing(3)
  },

  link: {
    fontSize: "0.75rem",
    lineHeight: "0.875rem",
    color: "#4D4D50",
    textDecoration: "underline",
    cursor: "pointer"
  },
  secureInfo: {
    color: "#07A35A",
    fontSize: "0.625rem",
    display: "flex",
    alignItems: "center",
    marginTop: theme.spacing(2),
    "& svg": {
      color: "#07A35A",
      width: "10px",
      height: "10px",
      marginRight: theme.spacing(0.5)
    }
  }
}));

function StripeSection({
  planName,
  currency,
  vehiclesCount,
  setLoading,
  isEditing,
  setBillingDetails,
  resetProject,
  handleClose,
  payment,
  setPayment,
  setInvoices,
  setUser,
  subscribed,
  setProjects,
  getProjectDetails
}) {
  const classes = useStyles();
  const history = useHistory();

  const userData = useSelector(state => state.auth.userData);
  const billingDetails = useSelector(state => state.account.billingDetails);
  const userCurrency =
    userData?.organisationSite &&
    userData.organisationSite.organisation.plan?.currency;
  const [paymentMethod, setPaymentMethod] = useState(
    userCurrency === "eur" ? payment?.pm_type || "card" : "card"
  );

  const recaptcha = useRef();

  let payloadRef = useRef({});

  const chosenPlan = plans.find(plan => plan.link === planName) || {};

  const validateInputs = () => {
    const currentErrors = {
      zipcode:
        isUS && !inputValues.zipcode.trim() ? "Please fill in this field" : "",
      cardholderName:
        paymentMethod !== "card"
          ? ""
          : !inputValues.cardholderName.trim()
          ? "Please fill in this field"
          : validateName(inputValues.cardholderName)
          ? "Cardholder name should not contain special characters"
          : "",
      cardNumber: paymentMethod !== "card" ? "" : cardNumber.empty,
      cardExpiry: paymentMethod !== "card" ? "" : cardExpiry.empty,
      cardCvc: paymentMethod !== "card" ? "" : cardCvc.empty,
      iban: paymentMethod !== "sepa_debit" ? "" : iban.empty,
      ibanFullName:
        paymentMethod !== "sepa_debit"
          ? ""
          : !inputValues.ibanFullName.trim()
          ? "Please fill in this field"
          : inputValues.ibanFullName.trim().length <= 2
          ? "Full name is invalid"
          : validateName(inputValues.ibanFullName)
          ? "Full name should not contain special characters"
          : "",
      ibanEmail:
        paymentMethod !== "sepa_debit"
          ? ""
          : !inputValues.ibanEmail.trim()
          ? "Please fill in this field"
          : !validateEmail(inputValues.ibanEmail)
          ? "Invalid email"
          : ""
    };

    setErrors({ ...currentErrors });
    return Object.keys(currentErrors).some(key => currentErrors[key]);
  };

  const stripe = useStripe();
  const elements = useElements();

  const clientSecret = useRef("");

  const [checked, setChecked] = useState(false);
  const [captchaLoaded, setCaptchaLoaded] = useState(false);

  const [cardNumber, setCardNumber] = useState({
    empty: true,
    error: ""
  });

  const [cardExpiry, setCardExpiry] = useState({
    empty: true,
    error: ""
  });

  const [cardCvc, setCardCvc] = useState({
    empty: true,
    error: ""
  });

  const [iban, setIban] = useState({
    empty: true,
    error: ""
  });

  const initialInputValues = useRef({
    address: "",
    address2: "",
    region: "",
    zipcode: "",
    city: "",
    vatID: "",
    cardholderName: "",
    ibanFullName: "",
    ibanEmail: userData.email
  });

  const [inputValues, setInputValues] = useState(initialInputValues.current);

  const { enqueueSnackbar } = useSnackbar();

  const generateInputValues = () => ({
    email: billingDetails.email || "",
    address: billingDetails.address_line1 || "",
    address2: billingDetails.address_line2 || "",
    zipcode: billingDetails.postal_code || "",
    city: billingDetails.city || "",
    vatID:
      billingDetails.valid_vat && billingDetails.vat_id
        ? billingDetails.vat_id.slice(2)
        : "",
    region: billingDetails.state || "",
    companyName: billingDetails.name || ""
  });

  useEffect(() => {
    if (checked) {
      setInputValues({ ...inputValues, ...generateInputValues() });
    } else {
      setInputValues({
        ...initialInputValues.current,
        cardholderName: inputValues.cardholderName,
        ibanFullName: inputValues.ibanFullName,
        ibanEmail: inputValues.ibanEmail
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checked]);

  const cardNumberRef = useRef();
  const cardExpiryRef = useRef();
  const cardCvcRef = useRef();
  const ibanRef = useRef();

  useEffect(() => {
    setSelectValues({
      country: userData.organisationSite
        ? userData.organisationSite.organisation.country || ""
        : ""
    });
  }, [userData]);

  const initialErrorValues = {
    address: "",
    region: "",
    zipcode: "",
    city: "",
    cardholderName: "",
    vatID: "",
    cardNumber: cardNumber.error,
    cardExpiry: cardExpiry.error,
    cardCvc: cardCvc.error,
    iban: iban.error,
    ibanFullName: "",
    ibanEmail: ""
  };

  const [errors, setErrors] = useState({ ...initialErrorValues });

  const [selectValues, setSelectValues] = useState({
    country: userData.organisationSite
      ? userData.organisationSite.organisation.country || ""
      : ""
  });

  const handleSelect = e => {
    setSelectValues({ ...selectValues, [e.target.name]: e.target.value });
  };

  const handleChange = e => {
    setInputValues({ ...inputValues, [e.target.name]: e.target.value });
  };

  const isUS = selectValues.country === "US";

  /**
   * 1st step after submit: validate inputs, check if vat id is valid from api
   */
  const handleSubmitCredit = () => {
    setLoading(true);
    const error = validateInputs();

    if (error) {
      setLoading(false);
      return;
    }

    let vatID = trimSpaces(inputValues.vatID);

    if (vatID.slice(0, 2).toUpperCase() === selectValues.country)
      vatID = vatID.slice(2);

    if (vatID) {
      API.post("/v1/billing/check/vat", {
        vat_number: selectValues.country + vatID
      }).then(res => {
        if (res.data) {
          handlePMCreation();
        } else {
          setErrors({
            ...initialErrorValues,
            vatID: "VAT number is not valid"
          });
          setLoading(false);
        }
      });
    } else {
      handlePMCreation();
    }
  };

  /**
   * 2nd step after submit: create Stripe pm, execute recaptcha
   */
  const handlePMCreation = async () => {
    let payload;
    if (paymentMethod === "card") {
      payload = await stripe.createPaymentMethod({
        type: "card",
        card: elements.getElement(CardNumberElement)
      });
      if (payload.error) {
        setLoading(false);
        console.log(payload.error);
        if (payload.error.type === "validation_error") {
          if (payload.error.code?.includes("expiry")) {
            setCardExpiry({
              ...cardExpiry,
              error: payload.error.message || "Something went wrong"
            });
          } else if (payload.error.code?.includes("cvc")) {
            setCardCvc({
              ...cardCvc,
              error: payload.error.message || "Something went wrong"
            });
          } else {
            setCardNumber({
              ...cardNumber,
              error: payload.error.message || "Your card has been declined."
            });
          }
        } else {
          setCardNumber({
            ...cardNumber,
            error: payload.error.message || "Your card has been declined."
          });
        }
        return;
      }
    } else {
      console.log("here", elements.getElement(IbanElement));
      payload = await stripe.createPaymentMethod({
        type: "sepa_debit",
        sepa_debit: elements.getElement(IbanElement),
        billing_details: {
          name: inputValues.ibanFullName,
          email: inputValues.ibanEmail
        }
      });
      console.log(payload);
      if (payload.error) {
        setLoading(false);
        setIban({
          ...iban,
          error: payload.error.message || "Something went wrong"
        });
        return;
      }
    }

    payloadRef.current = payload;
    if (process.env.NODE_ENV === "production" && !ENVIRONMENT === "dev") {
      if (captchaLoaded) {
        recaptcha.current.execute();
      } else {
        setLoading(false);
        enqueueSnackbar(
          "Google reCaptcha not loaded, please try to reload the page"
        );
      }
    } else {
      onResolved(true);
    }
  };

  const handleSetPaymentMethod = newPaymentMethod => {
    if (newPaymentMethod === "sepa_debit") {
      setCardExpiry({
        empty: true,
        error: ""
      });
      setCardCvc({
        empty: true,
        error: ""
      });
      setCardNumber({
        empty: true,
        error: ""
      });
    } else {
      setIban({
        empty: true,
        error: ""
      });
    }
    setPaymentMethod(newPaymentMethod);
  };

  /**
   * 3rd step after submit: if recaptcha resolves, send and verify payment method from api, if needed, ask for 3d authentication
   * @param data google recaptcha token
   */
  const onResolved = data => {
    if (data) {
      setLoading(true);
      console.log(payloadRef);
      const body = {};
      if (paymentMethod === "card") {
        body.cardholder_name = inputValues.cardholderName;
      } else {
        body.full_name = inputValues.ibanFullName;
        body.email = inputValues.ibanEmail;
      }
      let vatID = trimSpaces(inputValues.vatID);

      if (vatID.slice(0, 2).toUpperCase() === selectValues.country)
        vatID = vatID.slice(2);

      API.post(
        `/v1/stripe/payments/${payloadRef.current.paymentMethod.id}`,
        body
      )
        .then(res => {
          const pmRes = res;

          API.patch("/v1/billing/details", {
            email: billingDetails.email || "",
            name:
              paymentMethod === "card"
                ? inputValues.cardholderName
                : inputValues.ibanFullName,
            city: inputValues.city.trim(),
            country: selectValues.country,
            address_line1: inputValues.address.trim(),
            address_line2: inputValues.address2.trim(),
            postal_code: inputValues.zipcode.trim(),
            state: inputValues.region.trim(),
            vat_number: vatID ? selectValues.country + vatID : ""
          })
            .then(res => {
              setBillingDetails(res.data);
              // Check please
              if (!res.data.vat_id || res.data.valid_vat) {
                // setBillingDetailsVisible(false);
              } else {
                setErrors({ ...errors, vatID: "VAT number is not valid" });
              }

              clientSecret.current = pmRes.data.client_secret;
              if (pmRes.data.action_required) {
                handleSubscribe(true);
              } else {
                handleSubscribe();
              }
            })
            .catch(err => {
              recaptcha.current.reset();

              setLoading(false);
              enqueueSnackbar(
                "Something went wrong, while updating billing details"
              );
            });
        })
        .catch(err => {
          console.dir(err);
          if (err && err.status === 400) {
            enqueueSnackbar(err.data.message);
            setCardNumber({ ...cardNumber, error: " " });
            setCardExpiry({ ...cardExpiry, error: " " });
            setCardCvc({ ...cardCvc, error: " " });
          }
          recaptcha.current.reset();
          setLoading(false);
        });
    } else {
      recaptcha.current.reset();

      setLoading(false);
    }
  };

  const check3d = data => {
    const { method, property } = data.client_secret.includes("seti_")
      ? {
          method: "confirmCardSetup",
          property: "setupIntent"
        }
      : {
          method: "confirmCardPayment",
          property: "paymentIntent"
        };
    stripe[method](data.client_secret, {
      payment_method: payloadRef.current.paymentMethod.id
    }).then(function (response) {
      if (response.error) {
        console.log(response.error);
        recaptcha.current.reset();

        enqueueSnackbar("Payment failed, something went wrong.");
        setLoading(false);
        fetchProfileAndMakeSuccessRequests();
      } else if (
        response[property] &&
        response[property].status === "succeeded"
      ) {
        fetchProfileAndMakeSuccessRequests();
      } else {
        enqueueSnackbar("Couldn't verify payment, please try again.");
        setLoading(false);
        recaptcha.current.reset();
      }

      // Handle result.error or result.paymentIntent
    });
  };

  const fetchProfileAndMakeSuccessRequests = () => {
    let counter = 0;
    return (async function innerFunc() {
      try {
        const res = await API.get("v1/users/profile/");
        counter++;
        if (
          res.data &&
          res.data.organisationSite.organisation
            .subscription_last_payment_status === "processing" &&
          counter <= 2
        ) {
          setTimeout(innerFunc, 5000);
        } else {
          setUser(res.data);
          handleSuccessRequests(res.data);
        }
      } catch (e) {
        enqueueSnackbar(
          "Something went wrong while fethcing user's profile information"
        );
        setLoading(false);
      }
    })();
  };

  const handlePaymentConfirm = () => {
    API.post(
      `/v1/stripe/payments/confirm/${payloadRef.current.paymentMethod.id}`
    )
      .then(res => {
        if (res.data.client_secret) {
          check3d(res.data);
        } else {
          fetchProfileAndMakeSuccessRequests();
        }
      })
      .catch(err => console.log(err));
  };

  /**
   *  4th step after submit: if only editing payment method, go right to 5th step, else create or update subscription
   */
  const handleSubscribe = is3dAuth => {
    if (isEditing) {
      if (is3dAuth) {
        handlePaymentConfirm(isEditing);
      } else {
        fetchProfileAndMakeSuccessRequests();
      }
    } else {
      // Define method
      const method = subscribed ? "patch" : "post";
      API[method]("/v1/stripe/subscriptions", {
        plan_type: chosenPlan.name,
        currency: currency.string.toLowerCase(),
        vehicles: chosenPlan.name === "individual" ? 1 : vehiclesCount
      })
        .then(res => {
          // resetProject();
          console.log(res);
          if (res.data.action_required || is3dAuth) {
            handlePaymentConfirm(isEditing);
          } else {
            fetchProfileAndMakeSuccessRequests();
          }
        })
        .catch(err => {
          recaptcha.current.reset();
          enqueueSnackbar("Something went wrong while creating subscription.");
          console.log(err);
          setLoading(false);
        });
    }
  };

  /**
   * 5th step after submit: if everything is successfull, update user information and show success message/window
   * @param {object} [freshUserData] User data to update
   */

  const handleSuccessRequests = freshUserData => {
    Promise.all([
      API.get("v1/stripe/payments/"),
      API.get("v1/stripe/invoices/")
    ])
      .then(res => {
        const payment = res[0].data.find(payment => payment.default_pm);
        setPayment(payment);
        setInvoices(res[1].data);
      })
      .catch(err => console.log(err))
      .finally(() => {
        setLoading(false);
        // const userDataTemp = freshUserData || userData;
        // setUser(userDataTemp);

        if (
          freshUserData &&
          freshUserData.organisationSite.organisation
            .subscription_last_payment_status === "requires_action" &&
          freshUserData.organisationSite.organisation.subscription_status ===
            "incomplete"
        ) {
          handleClose();
          enqueueSnackbar("Couldn't verify payment");
        } else if (isEditing) {
          handleClose();
          enqueueSnackbar("Billing details updated");
        } else {
          if (chosenPlan.name === plans[0].name) {
            sessionStorage.removeItem("currentProject");
            resetProject();
            getProjectDetails();
          }
          if (
            freshUserData &&
            ["succeeded", "active"].includes(
              freshUserData.organisationSite.organisation.subscription_status
            )
          ) {
            history.replace("/payment/success", {
              chosenPlan: chosenPlan.title,
              currency: currency.symbol,
              action:
                freshUserData.organisationSite.organisation
                  .subscription_last_payment_status === "processing"
                  ? "processing"
                  : undefined,
              isSepa: paymentMethod === "sepa_debit",
              activating:
                userData.organisationSite.organisation?.subscription_status ===
                  "trialing" ||
                userData.organisationSite.organisation?.subscription_status ===
                  "canceled"
            });
          } else {
            history.push("/");
          }
        }
      });
  };

  return planName && !chosenPlan.name ? (
    <Redirect to="/payment/select-plan" />
  ) : (
    <Box
      className="StripeWrapper"
      display="flex"
      flexDirection="column"
      alignItems="center"
      pb={3}
      overflow="auto"
    >
      <Box style={{ backgroundColor: "#f9f9f9" }} py={6} width="100%">
        <ReCAPTCHA
          ref={recaptcha}
          sitekey={
            window.Cypress
              ? "6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI"
              : "6Ldd0N8UAAAAAA0WJ0FvzVvi0sk4Ph0NlgStHY-n"
          }
          asyncScriptOnLoad={e => {
            if (e) {
              setCaptchaLoaded(e.loaded);
            }
          }}
          onChange={onResolved}
          size="invisible"
          theme="light"
          badge="bottomleft"
        />
        <StripeForm
          handleChange={handleChange}
          errors={errors}
          inputValues={inputValues}
          paymentMethod={paymentMethod}
          setPaymentMethod={handleSetPaymentMethod}
          cardData={{
            cardNumber,
            cardExpiry,
            cardCvc,
            iban,
            cardExpiryRef,
            cardNumberRef,
            cardCvcRef,
            ibanRef,
            setCardNumber,
            setCardExpiry,
            setCardCvc,
            setIban
          }}
          userCurrency={userCurrency}
          checked={checked}
          setChecked={setChecked}
          selectValues={selectValues}
          handleSelect={handleSelect}
          setErrors={setErrors}
          initialErrorValues={initialErrorValues}
        />
      </Box>
      <Box maxWidth={"866px"}>
        <Prorata
          isEditing={isEditing}
          currency={currency}
          paymentMethod={paymentMethod}
          vehiclesCount={vehiclesCount}
          chosenPlan={chosenPlan}
        />

        <Box
          display="flex"
          justifyContent="space-between"
          marginTop={isEditing ? 5 : 4}
          px={3}
        >
          <Box height={remCalc(36)} display="flex" alignItems="center">
            <RouterLink
              className={classes.link}
              to={isEditing ? "/billing" : `/payment/${chosenPlan.link}`}
            >
              Back to previous step
            </RouterLink>
          </Box>
          <Box display="flex" flexDirection="column" alignItems="flex-end">
            <Button
              variant="contained"
              color="primary"
              className={classes.btn}
              disableElevation
              onClick={handleSubmitCredit}
              id="billingDetails-submitButton"
            >
              {isEditing ? "Submit" : "Subscribe"}
            </Button>
            {paymentMethod === "card" && (
              <Typography className={classes.secureInfo}>
                <LockIcon /> Secure credit card payment
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

const mapStateToProps = state => ({
  subscribed:
    state.auth.userData.organisationSite &&
    state.auth.userData.organisationSite.organisation.subscription_id,
  payment: state.account.payment
});

export default connect(mapStateToProps, {
  setPayment,
  resetProject,
  setInvoices,
  setBillingDetails,
  setUser,
  getProjectDetails
})(StripeSection);
